import dayjs from "dayjs"

import type {
  BulkPriceChangeUpdateInput,
  Maybe,
  PriceChangeItemGroup,
  PriceChangeItems,
  SubmitPriceChangeItemInput,
  UpdateSubmissionItemInput,
} from "@/graphql/codegen/graphql"
import { PriceChangeType } from "@/graphql/codegen/graphql"
import type { PricingGridRow } from "@/screens/Pricing/components/tables/types"
import { PricingNumericTypeValues, SubmitConfirmation } from "@/screens/Pricing/constants"
import type { BulkUpdateFormValues } from "@/screens/Pricing/schema/BulkUpdateSchema"
import type { PriceChangeEdit } from "@/screens/Pricing/stores/usePriceTableEditsStore"

export const mapBulkUpdateFormValuesToInput = (formValues: BulkUpdateFormValues): BulkPriceChangeUpdateInput => {
  const {
    acceptedCost,
    acceptedCostType,
    priceChange,
    priceChangeType,
    maintainMargin,
    effectiveDate,
    expirationDate,
  } = formValues
  const hasAcceptedCost = typeof acceptedCost === "number"
  const hasPriceChange = typeof priceChange === "number"
  return {
    acceptedCostChangePercent:
      hasAcceptedCost && acceptedCostType === PricingNumericTypeValues.Percent ? acceptedCost : undefined,
    acceptedCostChangeDollar:
      hasAcceptedCost && acceptedCostType === PricingNumericTypeValues.Dollar ? acceptedCost : undefined,
    priceChangePercent:
      hasPriceChange && priceChangeType === PricingNumericTypeValues.Percent ? priceChange : undefined,
    priceChangeDollar: hasPriceChange && priceChangeType === PricingNumericTypeValues.Dollar ? priceChange : undefined,
    maintainMargins: maintainMargin || undefined,
    effectiveDate: effectiveDate ?? undefined,
    expirationDate: expirationDate ?? undefined,
  }
}

const toBasePriceChangeItem = (
  row: PricingGridRow,
  productId: number,
  group: PriceChangeItemGroup,
  uomId: string,
  priceChangeType: PriceChangeType,
  newAcceptedCost?: number | null
) => {
  const isPurchasingCost = priceChangeType === PriceChangeType.PurchasingCost
  const purchasingCostWarehouseAreaId = group.shipToWarehouseArea?.warehouseAreaId

  return {
    productId,
    customerId: row.item.customerId,
    qty: row.qty,
    price: row.newPrice ?? row.price,
    previousPrice: row.previousPrice,
    uomId,
    freightTerms: row.item.freightTerms ?? undefined,
    shipToId: isPurchasingCost ? (purchasingCostWarehouseAreaId ?? undefined) : undefined,
    billBack: row.item.billBack ?? undefined,
    previousBillBack: row.item.previousBillBack ?? undefined,
    shipFromId: isPurchasingCost
      ? (group.shipFromLocation?.locationId ?? "")
      : (group.shipFromWarehouseArea?.warehouseAreaId ?? ""),
    effectiveDate: row.item.effectiveDate ?? undefined,
    expirationDate: row.item.expirationDate ?? undefined,
    acceptedCost: newAcceptedCost ?? row.acceptedCost ?? undefined,
    previousAcceptedCost: group.acceptedCost ?? undefined,
    lastBareCost: group.lastBareCost ?? undefined,
    lastLaidDownCost: group.lastLaidDownCost ?? undefined,
    incoterms: group.incoterms ?? "",
    incotermsLocationId: group.incotermsLocationId ?? "",
    surcharge1Price: row.surcharge1Price ?? undefined,
    surcharge1Description: row.surcharge1Description ?? undefined,
    surcharge2Price: row.surcharge2Price ?? undefined,
    surcharge2Description: row.surcharge2Description ?? undefined,
    surcharge3Price: row.surcharge3Price ?? undefined,
    surcharge3Description: row.surcharge3Description ?? undefined,
    changeNote: row.item.changeNote ?? undefined,
  }
}

export const toPriceChangeSubmitItem = (
  row: PricingGridRow,
  productId: number,
  group: PriceChangeItemGroup,
  uomId: string,
  priceChangeType: PriceChangeType
): SubmitPriceChangeItemInput => toBasePriceChangeItem(row, productId, group, uomId, priceChangeType)

export const toPriceChangeSubmitItems = (
  rows: PricingGridRow[],
  productId: number,
  group: PriceChangeItemGroup,
  uomId: string,
  priceChangeType: PriceChangeType,
  newAcceptedCost?: number | null
): SubmitPriceChangeItemInput[] =>
  rows.map((row) => toBasePriceChangeItem(row, productId, group, uomId, priceChangeType, newAcceptedCost))

export const toPriceChangeUpdateItems = (
  rows: PricingGridRow[],
  group: PriceChangeItemGroup,
  uomId: string,
  newAcceptedCost?: number | null
): UpdateSubmissionItemInput[] => {
  const tierRowsByCustomerRowId = new Map<string, PricingGridRow[]>()
  for (const row of rows) {
    const tierRows = tierRowsByCustomerRowId.get(row.item.id)
    if (tierRows) {
      tierRows.push(row)
    } else {
      tierRowsByCustomerRowId.set(row.item.id, [row])
    }
  }

  return [...tierRowsByCustomerRowId.entries()].map(([customerRowId, tierRows]) => {
    const firstRow = tierRows[0]
    return {
      customerRowId,
      freightTerms: firstRow.item.freightTerms ?? undefined,
      billBack: firstRow.item.billBack ?? undefined,
      previousBillBack: firstRow.item.previousBillBack ?? undefined,
      // The backend reads an omitted date as "keep stored"; "" is how a cleared date reaches it.
      effectiveDate: firstRow.item.effectiveDate ?? "",
      expirationDate: firstRow.item.expirationDate ?? undefined,
      acceptedCost: newAcceptedCost ?? firstRow.acceptedCost ?? undefined,
      previousAcceptedCost: group.acceptedCost ?? undefined,
      changeNote: firstRow.item.changeNote ?? undefined,

      tiers: tierRows.map((row) => ({
        qty: row.qty,
        uomId,
        price: row.newPrice ?? row.price,
        previousPrice: row.previousPrice,
        surcharge1Price: row.surcharge1Price ?? undefined,
        surcharge1Description: row.surcharge1Description ?? undefined,
        surcharge2Price: row.surcharge2Price ?? undefined,
        surcharge2Description: row.surcharge2Description ?? undefined,
        surcharge3Price: row.surcharge3Price ?? undefined,
        surcharge3Description: row.surcharge3Description ?? undefined,
      })),
    }
  })
}

export const calculateTotalPricingVariations = (priceChangeItems: PriceChangeItems[] | null): number => {
  if (!priceChangeItems) {
    return 0
  }

  return priceChangeItems.reduce((acc, pcItems) => {
    const spanKeys = new Set(
      pcItems.groups.flatMap((group) => group.items.map((item) => `${group.id}::${item.priceChangeItemId}`))
    )
    return acc + spanKeys.size
  }, 0)
}

export const calculateInitialUOM = (
  priceChangeItemsGroups: PriceChangeItemGroup[],
  fallbackValue?: Maybe<string>
): string => {
  const initialUOM = priceChangeItemsGroups
    .flatMap((group) => group.items)
    .flatMap((item) => item.tiers)
    .find((tier) => tier.uomId)?.uomId

  return initialUOM ?? fallbackValue ?? ""
}

export const calculateModifiedTiersCount = (edits: Record<string, PriceChangeEdit>): number =>
  Object.values(edits).filter((edit) => edit.isModified).length

export const calculateInvalidTiersCount = (edits: Record<string, PriceChangeEdit>): number =>
  Object.values(edits).reduce((total, priceChangeEditItem) => {
    if (!priceChangeEditItem.row.item.effectiveDate) total++
    if (!priceChangeEditItem.row.qty || priceChangeEditItem.row.qty <= 0) total++

    return total
  }, 0)

export const checkInvalidEffectiveDates = (edits: Record<string, PriceChangeEdit>): boolean => {
  const currentDate = dayjs()

  return Object.values(edits).some(({ row }) => {
    const effectiveDate = row?.item?.effectiveDate

    return effectiveDate && dayjs(effectiveDate).isBefore(currentDate, "day")
  })
}

export const getNextSubmitConfirmation = ({
  edits,
  defaultedRowCount,
  lapsedConfirmed,
}: {
  edits: Record<string, PriceChangeEdit>
  defaultedRowCount: number
  lapsedConfirmed: boolean
}): SubmitConfirmation | null => {
  if (!lapsedConfirmed && checkInvalidEffectiveDates(edits)) return SubmitConfirmation.LapsedDate
  if (defaultedRowCount > 0) return SubmitConfirmation.DefaultedDate
  return null
}
